import {
  CircleCheckIcon,
  ExternalLinkIcon,
  RepeatIcon,
  Trash2Icon,
} from 'lucide-react';
import { Badge, Button, Card, CopyButton } from '@tinct/web-components';
import { cn } from '@tinct/core-utils';

export type ActiveDomainCardProps = {
  className?: string;
  domain: string;
  baseUrl: string;
  onReplace: () => void;
  onRemove: () => void;
};

/**
 * Success state: the custom domain is live. Every published page of the
 * workspace serves at `{baseUrl}{page-slug}/` — automatically, including
 * pages published later.
 */
export function ActiveDomainCard({
  className,
  domain,
  baseUrl,
  onReplace,
  onRemove,
}: ActiveDomainCardProps) {
  return (
    <Card className={cn('gap-0 py-0', className)}>
      <div className="flex items-start justify-between gap-4 px-6 pt-6">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-3">
            <h3 className="font-heading text-xl font-semibold leading-7 tracking-[-0.3px] text-foreground">
              {domain}
            </h3>
            <Badge className="bg-teal-600 text-white">Active</Badge>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-6 py-6">
        <div className="flex items-start gap-4 rounded-2xl border border-teal-100 bg-[var(--colors-success-background)] p-4">
          <div className="pt-0.5">
            <CircleCheckIcon className="size-4 text-teal-600" />
          </div>
          <div className="flex flex-1 flex-col gap-2 text-sm leading-5 text-teal-600">
            <span className="font-medium">Your domain is live</span>
            <span>
              Every published page is served at{' '}
              <code className="rounded bg-teal-600/10 px-1 py-0.5 text-xs">
                {baseUrl}
                &#123;page-slug&#125;/
              </code>{' '}
              — pages you publish later appear there automatically.
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">Base URL</span>
          <a
            href={baseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-sm text-foreground underline underline-offset-2 hover:text-muted-foreground"
          >
            {baseUrl}
            <ExternalLinkIcon className="size-3.5" />
          </a>
          <CopyButton value={baseUrl} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t px-6 py-6">
        <Button variant="outline" onClick={onRemove}>
          <Trash2Icon className="size-4" />
          Remove domain
        </Button>
        <Button variant="outline" onClick={onReplace}>
          <RepeatIcon className="size-4" />
          Replace domain
        </Button>
      </div>
    </Card>
  );
}
